import React, { useState, useRef, useEffect, useReducer, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { Sidebar } from './Dashboard';
import { tusUpload } from '../lib/tusUpload';
import { fetchFinalizeStatusBatch } from '../lib/finalizeStatus';
import { createMintGate } from '../lib/mintGate';

const API = process.env.REACT_APP_API_URL || 'https://tribaltech-backend.onrender.com';

// Worker pool size. Upload work is bandwidth-bound — 2 keeps the
// upstream pipe saturated while one worker is parked on a pacing /
// 429 wait, without the extra memory + failure blast-radius of more
// concurrent TUS streams. Tune here if monitoring shows idle
// bandwidth.
const UPLOAD_CONCURRENCY = 2;

const MINT_MAX_ATTEMPTS      = 5;      // 429 budget: 1 initial + 4 retries
const TRANSIENT_MAX_ATTEMPTS = 3;      // network/5xx: 1 initial + 2 retries
const POLL_INTERVAL_MS       = 3000;   // batch status poll cadence
const POLL_BATCH             = 50;     // mirrors the backend job_ids cap
const RATE_LIMIT_FALLBACK_MS = 20000;  // used when a 429 omits Retry-After

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Retryable-transient = a network error (no response) or a 5xx.
// 4xx other than 429 (handled separately) is not retryable.
const isTransient = (status) => status === undefined || status >= 500;

// Exponential backoff for transient retries: 2s, 4s, 8s, …
const backoffMs = (retryNum) => 2000 * (2 ** (retryNum - 1));

// Milliseconds to wait after a 429 — server-directed: the
// Retry-After header, then the retry_after_seconds body field, then
// a fallback.
const retryAfterMs = (err) => {
  const hdr = parseInt(err?.response?.headers?.['retry-after'], 10);
  if (Number.isFinite(hdr) && hdr > 0) return hdr * 1000;
  const body = err?.response?.data?.retry_after_seconds;
  if (Number.isFinite(body) && body > 0) return body * 1000;
  return RATE_LIMIT_FALLBACK_MS;
};

// Stage 3d Path A — creator master upload page (gate 2).
//
// Per-file lifecycle:
//   queued → minting → uploading → finalizing → fingerprinting
//          → catalogued      (failed is terminal from any of the
//                             first five)
//
// A pool of UPLOAD_CONCURRENCY workers drains a ref-held queue. Each
// worker runs mint → TUS upload → finalize for one file, hands the
// job_id to the batch poller, then takes the next file — workers
// never block on the 2-4 min fingerprint/sweep wait. The MintGate
// paces /upload-token calls against the server's 15-per-300s limit.
export default function MastersUpload() {
  // The queue lives in a ref (not state) so the worker pool can read
  // and claim files with no stale-closure races; forceRender mirrors
  // it to the screen after every mutation.
  const queueRef = useRef([]);
  const [, forceRender] = useReducer((n) => n + 1, 0);

  const [poolActive, setPoolActive]         = useState(false);
  const [masters, setMasters]               = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(true);

  // Gate 3 — slot accounting + per-row activation state.
  const [plan, setPlan]                 = useState(null);
  const [slotLimit, setSlotLimit]       = useState(0);
  const [slotsUsed, setSlotsUsed]       = useState(0);
  const [busyMasterId, setBusyMasterId] = useState(null);  // master id mid-action
  const [rowError, setRowError]         = useState(null);  // {masterId, message}

  // L2 (2026-06-13): silently-failed uploads from the server. Each is
  // { job_id, filename, uploaded_at, failed_at, size_bytes,
  //   classification: { kind, message } }
  //
  // Dismiss safety: confirmJobId tracks which row is mid-confirm
  // (renders "Dismiss anyway?" instead of one-click vanish). dismissed*
  // tracks the "show dismissed" toggle + restore-able list.
  const [failedUploads, setFailedUploads]       = useState([]);
  const [dismissedUploads, setDismissedUploads] = useState([]);
  const [dismissedCount, setDismissedCount]     = useState(0);
  const [showDismissed, setShowDismissed]       = useState(false);
  const [confirmJobId, setConfirmJobId]         = useState(null);
  const [dismissingJobId, setDismissingJobId]   = useState(null);
  const [restoringJobId, setRestoringJobId]     = useState(null);

  const activeWorkersRef = useRef(0);
  const pollerRef        = useRef(null);
  const tickerRef        = useRef(null);
  const mintGateRef      = useRef(null);
  if (!mintGateRef.current) mintGateRef.current = createMintGate();

  const appToken = localStorage.getItem('tribal_token');

  const patch = (item, fields) => { Object.assign(item, fields); forceRender(); };

  // ── masters list ───────────────────────────────────────────
  const loadMasters = useCallback((opts) => {
    const wantDismissed = (opts && opts.includeDismissed) || showDismissed;
    const url = API + '/api/masters'
      + (wantDismissed ? '?include_dismissed=true' : '');
    axios.get(url,
      { headers: { Authorization: 'Bearer ' + appToken } })
      .then((r) => {
        const d = r.data || {};
        setMasters(d.masters || []);
        setPlan(d.plan || null);
        setSlotLimit(d.slot_limit || 0);
        setSlotsUsed(d.slots_used || 0);
        setFailedUploads(d.failed_uploads || []);
        setDismissedCount(d.dismissed_count || 0);
        if (Array.isArray(d.dismissed_uploads)) {
          setDismissedUploads(d.dismissed_uploads);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMasters(false));
  }, [appToken, showDismissed]);

  // L2 (2026-06-13): dismiss a failed-upload row.
  //
  // Safety design: two-click confirm — first click sets confirmJobId
  // which swaps the row's button for "Dismiss anyway? [Yes] [Cancel]".
  // Yes triggers actuallyDismiss. Cancel clears confirmJobId. Prevents
  // a one-misclick from permanently re-hiding an unprotected scene
  // (the silent-gap bug recreated user-side, what L2 exists to fix).
  // The dismiss is ALSO reversible via "show dismissed" + Restore.
  const requestDismiss = (jobId) => { setConfirmJobId(jobId); };
  const cancelDismiss  = ()      => { setConfirmJobId(null); };
  const actuallyDismiss = async (jobId) => {
    setDismissingJobId(jobId);
    const prevFailed = failedUploads;
    setFailedUploads(failedUploads.filter((f) => f.job_id !== jobId));
    setConfirmJobId(null);
    try {
      await axios.post(API + '/api/masters/failed/' + jobId + '/dismiss',
        {}, { headers: { Authorization: 'Bearer ' + appToken } });
      // Refresh so dismissed_count + dismissed_uploads stay in sync.
      loadMasters();
    } catch {
      setFailedUploads(prevFailed);
    } finally {
      setDismissingJobId(null);
    }
  };

  // Restore a previously-dismissed row back to the failed_uploads
  // surface. Optimistic UI; reload after to refresh counts.
  const restoreDismissed = async (jobId) => {
    setRestoringJobId(jobId);
    const prevDismissed = dismissedUploads;
    setDismissedUploads(dismissedUploads.filter((f) => f.job_id !== jobId));
    try {
      await axios.post(API + '/api/masters/failed/' + jobId + '/undismiss',
        {}, { headers: { Authorization: 'Bearer ' + appToken } });
      loadMasters({ includeDismissed: true });
    } catch {
      setDismissedUploads(prevDismissed);
    } finally {
      setRestoringJobId(null);
    }
  };

  // Toggle the dismissed section. When opening for the first time we
  // need to fetch (the bare GET response omits dismissed_uploads).
  const toggleShowDismissed = () => {
    const next = !showDismissed;
    setShowDismissed(next);
    if (next) loadMasters({ includeDismissed: true });
  };

  useEffect(() => { loadMasters(); }, [loadMasters]);

  // Clear background intervals if the page unmounts mid-batch.
  useEffect(() => () => {
    clearInterval(pollerRef.current);
    clearInterval(tickerRef.current);
  }, []);

  // ── slot UX + per-row activation (gate 3) ──────────────────
  const planLabel = (p) => {
    if (p === 'clean_slate') return 'Clean Slate';
    if (typeof p === 'string' && p) return p.charAt(0).toUpperCase() + p.slice(1);
    return 'Your plan';
  };

  // Activate / deactivate a master, then re-fetch so the list + the
  // slot counts stay consistent. busyMasterId disables every action
  // button while one call is in flight (no double-click, no races).
  const activateMaster = async (masterId) => {
    setBusyMasterId(masterId);
    setRowError(null);
    try {
      await axios.post(API + '/api/masters/activate',
        { master_id: masterId }, { headers: authHeaders() });
      loadMasters();
    } catch (err) {
      const serverMsg = err?.response?.data?.error || '';
      const msg = (err?.response?.status === 409 && serverMsg.includes('no slots available'))
        ? `all ${slotLimit} slots in use — deactivate one first`
        : (serverMsg || 'activation failed');
      setRowError({ masterId, message: msg });
    } finally {
      setBusyMasterId(null);
    }
  };

  const deactivateMaster = async (masterId) => {
    setBusyMasterId(masterId);
    setRowError(null);
    try {
      await axios.post(API + '/api/masters/deactivate',
        { master_id: masterId }, { headers: authHeaders() });
      loadMasters();
    } catch (err) {
      const serverMsg = err?.response?.data?.error || '';
      const msg = (err?.response?.status === 409 && serverMsg.includes('30-day minimum'))
        ? "can't deactivate yet — must stay active 30 days"
        : (serverMsg || 'deactivation failed');
      setRowError({ masterId, message: msg });
    } finally {
      setBusyMasterId(null);
    }
  };

  // ── dropzone — appends to the queue (during a run too) ──────
  const onDrop = useCallback((accepted) => {
    const items = accepted.map((f, i) => ({
      id: `${Date.now()}-${i}-${f.name}`,
      file: f,
      name: f.name,
      sizeMB: (f.size / 1024 / 1024).toFixed(1),
      status: 'queued',
      progress: 0,
      jobId: null,
      masterId: null,
      error: null,
      waitUntil: null,
    }));
    queueRef.current = [...queueRef.current, ...items];
    forceRender();
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/*': ['.mp4', '.mov', '.mkv'], 'image/*': ['.jpg', '.jpeg', '.png'] },
  });

  // ── countdown ticker — 1s, runs only while a row is waiting ──
  const ensureTicker = () => {
    if (tickerRef.current) return;
    tickerRef.current = setInterval(() => {
      const stillWaiting = queueRef.current.some(
        (it) => it.waitUntil && it.waitUntil > Date.now());
      forceRender();
      if (!stillWaiting) {
        clearInterval(tickerRef.current);
        tickerRef.current = null;
      }
    }, 1000);
  };

  // ── batch status poller — 3s, runs only while jobs in flight ─
  const ensurePoller = () => {
    if (pollerRef.current) return;
    pollerRef.current = setInterval(async () => {
      const inflight = queueRef.current.filter(
        (it) => it.status === 'fingerprinting' && it.jobId);
      if (inflight.length === 0) {
        clearInterval(pollerRef.current);
        pollerRef.current = null;
        return;
      }
      try {
        const ids = inflight.map((it) => it.jobId);
        const jobs = [];
        for (let i = 0; i < ids.length; i += POLL_BATCH) {
          const part = await fetchFinalizeStatusBatch({
            apiBase: API, jobIds: ids.slice(i, i + POLL_BATCH), appToken });
          jobs.push(...part);
        }
        let cataloguedAny = false;
        for (const job of jobs) {
          const item = queueRef.current.find((it) => it.jobId === job.job_id);
          if (!item || item.status !== 'fingerprinting') continue;
          if (job.status === 'complete') {
            Object.assign(item, { status: 'catalogued', masterId: job.master_id });
            cataloguedAny = true;
          } else if (job.status === 'failed') {
            Object.assign(item, {
              status: 'failed',
              error: job.error_text || 'fingerprinting failed',
            });
          }
        }
        forceRender();
        if (cataloguedAny) loadMasters();
      } catch {
        // network blip — rows stay 'fingerprinting', retried next tick
      }
    }, POLL_INTERVAL_MS);
  };

  // ── per-file pipeline ──────────────────────────────────────
  const authHeaders = () => ({ Authorization: 'Bearer ' + appToken });

  const buildObjectName = (mint, item) => {
    const safe = item.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    return `${mint.path_prefix}${Date.now()}_${safe}`;
  };

  // Mint /upload-token through the MintGate, with 429 + transient
  // retry. Resolves with the token payload, throws on exhaustion.
  const mintWithRetry = async (item) => {
    let n429 = 0;
    let nTransient = 0;
    for (;;) {
      patch(item, { status: 'minting' });
      // pacing gate — onWait drives the countdown UI
      await mintGateRef.current.acquire((untilTs) => {
        patch(item, { waitUntil: untilTs });
        ensureTicker();
      });
      patch(item, { waitUntil: null });
      try {
        const { data } = await axios.post(
          API + '/api/masters/upload-token', {}, { headers: authHeaders() });
        return data;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 429) {
          n429 += 1;
          if (n429 >= MINT_MAX_ATTEMPTS) {
            throw new Error(
              "rate-limited — couldn't get an upload slot, try this file again later");
          }
          const baseMs = retryAfterMs(err);
          mintGateRef.current.penalize(baseMs);
          const waitMs = Math.max(0, baseMs + (Math.random() * 4 - 2) * 1000);
          patch(item, { waitUntil: Date.now() + waitMs });
          ensureTicker();
          await sleep(waitMs);
          patch(item, { waitUntil: null });
          continue;
        }
        if (isTransient(status)) {
          nTransient += 1;
          if (nTransient >= TRANSIENT_MAX_ATTEMPTS) {
            throw new Error('upload service unavailable — retry this file later');
          }
          await sleep(backoffMs(nTransient));
          continue;
        }
        throw new Error(
          err?.response?.data?.error || `upload-token failed (HTTP ${status})`);
      }
    }
  };

  // POST /finalize with transient retry (finalize is not rate-limited).
  const finalizeWithRetry = async (objectName) => {
    let nTransient = 0;
    for (;;) {
      try {
        const { data } = await axios.post(
          API + '/api/masters/finalize',
          { storage_path: objectName }, { headers: authHeaders() });
        return data;
      } catch (err) {
        const status = err?.response?.status;
        if (isTransient(status)) {
          nTransient += 1;
          if (nTransient >= TRANSIENT_MAX_ATTEMPTS) {
            throw new Error('finalize failed — upload service unavailable, retry later');
          }
          await sleep(backoffMs(nTransient));
          continue;
        }
        throw new Error(
          err?.response?.data?.error || `finalize failed (HTTP ${status})`);
      }
    }
  };

  // Full pipeline for one file. Self-contained try/catch — a failure
  // here fails only this file; the worker moves on to the next.
  const processFile = async (item) => {
    try {
      const mint = await mintWithRetry(item);
      const objectName = buildObjectName(mint, item);
      patch(item, { status: 'uploading', progress: 0 });
      await tusUpload({
        file: item.file,
        token: mint.token,
        bucket: mint.bucket,
        objectName,
        contentType: item.file.type,
        onProgress: (pct) => patch(item, { progress: pct }),
      });
      patch(item, { status: 'finalizing' });
      const fin = await finalizeWithRetry(objectName);
      patch(item, { status: 'fingerprinting', jobId: fin.job_id, waitUntil: null });
      ensurePoller();
    } catch (err) {
      patch(item, {
        status: 'failed',
        waitUntil: null,
        error: err?.message || 'Upload failed',
      });
    }
  };

  // ── worker pool ────────────────────────────────────────────
  const claimNextFile = () => {
    const item = queueRef.current.find((it) => it.status === 'queued');
    if (item) patch(item, { status: 'minting' });
    return item || null;
  };

  const runWorker = async () => {
    for (;;) {
      const item = claimNextFile();
      if (!item) break;
      await processFile(item);
    }
    activeWorkersRef.current -= 1;
    if (activeWorkersRef.current === 0) setPoolActive(false);
  };

  // "Upload all" — starts/resumes the pool. Workers drain the queue
  // continuously, so files dropped mid-run are picked up too.
  const startPool = () => {
    const want = UPLOAD_CONCURRENCY - activeWorkersRef.current;
    if (want <= 0) return;
    setPoolActive(true);
    for (let i = 0; i < want; i += 1) {
      activeWorkersRef.current += 1;
      runWorker();
    }
  };

  // ── render ─────────────────────────────────────────────────
  const statusText = (item) => {
    if (item.status === 'minting') {
      if (item.waitUntil && item.waitUntil > Date.now()) {
        const secs = Math.ceil((item.waitUntil - Date.now()) / 1000);
        return `Waiting for rate limit — ${secs}s`;
      }
      return 'Starting…';
    }
    if (item.status === 'uploading')      return `Uploading ${item.progress}%`;
    if (item.status === 'finalizing')     return 'Finalizing…';
    if (item.status === 'fingerprinting') return 'Fingerprinting…';
    if (item.status === 'catalogued')     return 'Catalogued';
    if (item.status === 'failed')         return 'Failed';
    return 'Queued';
  };

  const fmtBytes = (b) => (b == null ? '—' : (b / 1024 / 1024).toFixed(1) + ' MB');
  const fmtDur = (s) => (s == null ? '—' : `${Math.floor(s / 60)}m ${s % 60}s`);

  const queue       = queueRef.current;
  const queuedCount = queue.filter((q) => q.status === 'queued').length;
  const doneCount   = queue.filter(
    (q) => q.status === 'catalogued' || q.status === 'failed').length;

  return (
    <div className="page">
      <Sidebar />
      <div className="main">
        <div className="page-title">Protected masters</div>

        {/* slot line (gate 3) */}
        {!loadingMasters && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>
              {slotLimit > 0
                ? `${planLabel(plan)} · ${slotsUsed} of ${slotLimit} slots in use`
                : 'Your current plan has no monitoring slots.'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 20 }}>
              {slotLimit > 0
                ? 'Upload as many as you want — activate the ones you want monitored for leaks.'
                : 'Upgrade to activate masters for leak scanning.'}
            </div>
          </>
        )}

        {/* onboarding banner — empty state only (gate 3) */}
        {!loadingMasters && masters.length === 0 && (
          <div style={{
            background: 'rgba(80,130,200,0.08)',
            border: '1px solid rgba(80,130,200,0.25)',
            borderRadius: 10, padding: '14px 16px', marginBottom: 20,
            fontSize: 13, color: 'var(--text)', lineHeight: 1.55,
          }}>
            {slotLimit > 0
              ? `Upload your back catalog. Creator Protect scans for leaks of these videos too, not just new content. Scanning starts when you activate a master; your ${planLabel(plan)} plan covers ${slotLimit} active at once.`
              : 'Your current plan has no monitoring slots. Upgrade to activate masters for leak scanning.'}
          </div>
        )}

        {/* dropzone */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div
            {...getRootProps()}
            style={{
              border: '2px dashed ' + (isDragActive ? 'var(--purple)' : 'var(--border)'),
              borderRadius: 12,
              padding: '48px 24px',
              textAlign: 'center',
              cursor: 'pointer',
              background: isDragActive ? '#1A1A30' : 'transparent',
              transition: 'all 0.15s',
            }}
          >
            <input {...getInputProps()} />
            <div style={{ fontSize: 32, marginBottom: 12 }}>↑</div>
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              {isDragActive ? 'Drop files here' : 'Drag and drop videos or photos'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              MP4, MOV, MKV, JPG, PNG · resumable upload — large masters supported
            </div>
          </div>
        </div>

        {/* upload queue */}
        {queue.length > 0 && (
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 14,
            }}>
              <h3 style={{ fontSize: 15, margin: 0 }}>
                Upload queue
                <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 8 }}>
                  {doneCount} / {queue.length} done
                </span>
              </h3>
              <button
                className="btn btn-primary"
                onClick={startPool}
                disabled={poolActive || queuedCount === 0}
              >
                {poolActive ? 'Uploading…' : `Upload all (${queuedCount})`}
              </button>
            </div>
            {queue.map((q) => {
              const badgeClass = q.status === 'catalogued' ? 'badge-green'
                : q.status === 'failed' ? 'badge-red'
                : 'badge-purple';
              return (
                <div key={q.id} style={{
                  padding: '10px 14px', background: 'var(--bg2)',
                  borderRadius: 8, marginBottom: 6,
                }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div style={{ fontSize: 13 }}>{q.name}</div>
                    <span className={'badge ' + badgeClass}>{statusText(q)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    {q.sizeMB} MB
                    {q.status === 'failed' && q.error && (
                      <span style={{ color: '#f0a080' }}> · {q.error}</span>
                    )}
                  </div>
                  {q.status === 'uploading' && (
                    <div style={{
                      marginTop: 6, height: 4, borderRadius: 2, background: 'var(--border)',
                    }}>
                      <div style={{
                        width: q.progress + '%', height: '100%', borderRadius: 2,
                        background: 'var(--purple)', transition: 'width 0.2s',
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* L2 (2026-06-13): failed uploads — silently-failed jobs from
            prior sessions. Hidden when the list is empty so we don't
            add visual chrome for the common case (no failures).
            Dismiss is TWO-CLICK with a confirm step + reversible via
            the "show dismissed" toggle below — protects against the
            silent-gap bug being recreated by a subscriber misclick. */}
        {failedUploads.length > 0 && (
          <div className="card" style={{
            marginBottom: 24,
            borderLeft: '3px solid #f0a080',
          }}>
            <h3 style={{ fontSize: 15, marginBottom: 8 }}>
              {failedUploads.length === 1
                ? '1 upload didn\u2019t complete'
                : failedUploads.length + ' uploads didn\u2019t complete'}
            </h3>
            <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 14 }}>
              These files aren’t protected yet. Re-upload above to enroll,
              or dismiss if you don’t need them.
            </div>
            {failedUploads.map((f) => {
              const isConfirm   = confirmJobId   === f.job_id;
              const isDismissing = dismissingJobId === f.job_id;
              return (
                <div key={f.job_id} style={{
                  padding: '10px 14px', background: 'var(--bg2)',
                  borderRadius: 8, marginBottom: 6,
                }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: 12,
                  }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, overflow: 'hidden',
                                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.filename}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        {f.classification && f.classification.message}
                        {f.size_bytes ? ' \u00b7 ' + fmtBytes(f.size_bytes) : ''}
                        {f.failed_at ? ' \u00b7 failed ' +
                          new Date(f.failed_at).toLocaleDateString() : ''}
                      </div>
                    </div>
                    {!isConfirm && (
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        disabled={isDismissing}
                        onClick={() => requestDismiss(f.job_id)}
                      >
                        {isDismissing ? 'Dismissing\u2026' : 'Dismiss'}
                      </button>
                    )}
                  </div>
                  {isConfirm && (
                    <div style={{
                      marginTop: 10, padding: '8px 0 0', borderTop: '1px solid var(--border)',
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', gap: 12,
                    }}>
                      <div style={{ fontSize: 12, color: '#f0a080' }}>
                        This scene won’t be protected. Dismiss anyway?
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          disabled={isDismissing}
                          onClick={cancelDismiss}
                        >Cancel</button>
                        <button
                          className="btn btn-primary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          disabled={isDismissing}
                          onClick={() => actuallyDismiss(f.job_id)}
                        >{isDismissing ? 'Dismissing\u2026' : 'Yes, dismiss'}</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* L2 (2026-06-13): "show dismissed" toggle + restore section.
            Only rendered when the creator has at least one dismissed
            row — keeps the UI clean for the common no-failures case.
            The Restore action moves a row back into failed_uploads, so
            a mis-dismiss is fully reversible. */}
        {dismissedCount > 0 && (
          <div style={{ marginBottom: 24 }}>
            <button
              className="link"
              style={{
                background: 'none', border: 'none',
                color: 'var(--muted)', fontSize: 12,
                cursor: 'pointer', padding: 0, marginBottom: 8,
              }}
              onClick={toggleShowDismissed}
            >
              {showDismissed
                ? 'Hide dismissed (' + dismissedCount + ')'
                : 'Show ' + dismissedCount + ' previously dismissed'}
            </button>
            {showDismissed && (
              <div className="card" style={{ marginTop: 8 }}>
                <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 10 }}>
                  Dismissed failures. Restore any you want back on the list.
                </div>
                {dismissedUploads.map((f) => {
                  const isRestoring = restoringJobId === f.job_id;
                  return (
                    <div key={f.job_id} style={{
                      padding: '10px 14px', background: 'var(--bg2)',
                      borderRadius: 8, marginBottom: 6,
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', gap: 12,
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, overflow: 'hidden',
                                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.filename}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                          {f.classification && f.classification.message}
                          {f.dismissed_at ? ' \u00b7 dismissed ' +
                            new Date(f.dismissed_at).toLocaleDateString() : ''}
                        </div>
                      </div>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        disabled={isRestoring}
                        onClick={() => restoreDismissed(f.job_id)}
                      >
                        {isRestoring ? 'Restoring\u2026' : 'Restore'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* catalogued masters */}
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 16 }}>Your masters</h3>
          {loadingMasters ? (
            <div style={{ color: 'var(--muted)', padding: 20 }}>Loading…</div>
          ) : masters.length === 0 ? (
            <div style={{ color: 'var(--muted)', padding: '30px 0', textAlign: 'center' }}>
              No masters yet — upload your first above.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Uploaded</th><th>Duration</th><th>Size</th>
                  <th>Fingerprint</th><th>Status</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {masters.map((m) => (
                  <React.Fragment key={m.master_id}>
                    <tr>
                      <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ fontSize: 12 }}>{fmtDur(m.duration_seconds)}</td>
                      <td style={{ fontSize: 12 }}>{fmtBytes(m.file_bytes_source)}</td>
                      <td style={{ fontSize: 12 }}>
                        {m.phash_present && m.audio_present ? 'Video + audio'
                          : m.phash_present ? 'Video only'
                          : m.audio_present ? 'Audio only' : '—'}
                      </td>
                      <td>
                        {m.is_active
                          ? <span className="badge badge-green">Active</span>
                          : <span className="badge" style={{
                              background: 'var(--bg2)', color: 'var(--muted)' }}>Catalogued</span>}
                      </td>
                      <td>
                        {m.is_active ? (
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            disabled={busyMasterId !== null}
                            onClick={() => deactivateMaster(m.master_id)}
                          >
                            {busyMasterId === m.master_id ? 'Deactivating…' : 'Deactivate'}
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            disabled={busyMasterId !== null || slotLimit === 0}
                            title={slotLimit === 0
                              ? 'Upgrade your plan to activate masters for leak scanning'
                              : undefined}
                            onClick={() => activateMaster(m.master_id)}
                          >
                            {busyMasterId === m.master_id ? 'Activating…' : 'Activate'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {rowError && rowError.masterId === m.master_id && (
                      <tr>
                        <td colSpan={6} style={{
                          fontSize: 12, color: 'var(--coral)', padding: '0 0 8px',
                        }}>
                          ↳ {rowError.message}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
